import { CheckCircle2, Coins, RefreshCw, ShoppingCart } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../hooks';
import { billingService, contactService } from '../../services';
import { SubscriptionResponse, TokenPackage } from '../../types/entities';
import { Button, Card, Input, LoadingSpinner, Modal } from './';

interface BuyTokensModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BuyTokensModal: React.FC<BuyTokensModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const { currency } = useCurrency();
  const [packages, setPackages] = useState<TokenPackage[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [purchasingPackageId, setPurchasingPackageId] = useState<number | null>(
    null
  );
  const [showContactForm, setShowContactForm] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<{
    name: string;
    email: string;
    phone: string;
    message: string;
  }>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      message: '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      loadBillingData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setShowContactForm(false);
      reset();
    }
  }, [isOpen, reset]);

  const loadBillingData = useCallback(async (refresh: boolean = false) => {
    if (refresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const [packagesData, subscriptionData] = await Promise.all([
        billingService.getPackages(),
        billingService.getSubscription().catch(() => null),
      ]);

      setPackages(
        [...packagesData].sort(
          (a, b) => (a.price_usd || 0) - (b.price_usd || 0)
        )
      );
      setSubscription(subscriptionData);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t('tokens.errorLoadingPackages');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [t]);

  const getPriceByCurrency = (pkg: TokenPackage): { amount: number; code: string } => {
    const normalizedCurrency = currency.toUpperCase();

    if (normalizedCurrency === 'BRL' && typeof pkg.price_brl === 'number') {
      return { amount: pkg.price_brl, code: 'BRL' };
    }

    if (normalizedCurrency === 'EUR' && typeof pkg.price_eur === 'number') {
      return { amount: pkg.price_eur, code: 'EUR' };
    }

    return { amount: pkg.price_usd, code: 'USD' };
  };

  const formatPrice = (pkg: TokenPackage): string => {
    const price = getPriceByCurrency(pkg);
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: price.code,
      minimumFractionDigits: 2,
    }).format(price.amount);
  };

  const intervalLabel = (pkg: TokenPackage): string => {
    if (!pkg.interval) {
      return t('subscription.intervalMonthly');
    }

    if (pkg.interval === 'year') {
      return t('subscription.intervalYearly');
    }

    return t('subscription.intervalMonthly');
  };

  const currentPackageId = useMemo(
    () => (subscription?.package_id ? Number(subscription.package_id) : null),
    [subscription]
  );

  const handleCheckoutSubscription = async (pkg: TokenPackage) => {
    setPurchasingPackageId(pkg.id);

    try {
      const purchaseResponse = await billingService.purchasePackage(
        pkg.id,
        currency.toLowerCase()
      );

      toast.success(
        t('subscription.redirectingToCheckout', {
          name: purchaseResponse.package_name,
        })
      );

      onClose();

      setTimeout(() => {
        window.location.href = purchaseResponse.checkout_url;
      }, 300);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t('subscription.errorCheckout');
      toast.error(errorMessage);
      setPurchasingPackageId(null);
    }
  };

  const handleSubmitContact = async (values: {
    name: string;
    email: string;
    phone: string;
    message: string;
  }) => {
    try {
      await contactService.submitContact(values);
      toast.success(t('tokens.contactSuccess'));
      setShowContactForm(false);
      reset();
      onClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t('tokens.contactError');
      toast.error(errorMessage);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('subscription.managePlans')}
      size="xl"
    >
      <div className="flex flex-col h-full">
        {showContactForm ? (
          <form onSubmit={handleSubmit(handleSubmitContact)} className="space-y-4">
            <Input
              label={t('tokens.contactName')}
              placeholder={t('tokens.contactNamePlaceholder')}
              error={errors.name?.message}
              {...register('name', { required: t('tokens.contactNameRequired') })}
              required
            />
            <Input
              label={t('tokens.contactEmail')}
              placeholder={t('tokens.contactEmailPlaceholder')}
              error={errors.email?.message}
              type="email"
              {...register('email', {
                required: t('tokens.contactEmailRequired'),
                pattern: {
                  value: /^\S+@\S+\.\S+$/,
                  message: t('tokens.contactEmailInvalid'),
                },
              })}
              required
            />
            <Input
              label={t('tokens.contactPhone')}
              placeholder={t('tokens.contactPhonePlaceholder')}
              error={errors.phone?.message}
              {...register('phone', { required: t('tokens.contactPhoneRequired') })}
              required
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tokens.contactMessage')}
              </label>
              <textarea
                rows={4}
                className="w-full px-3 py-2 border border-light-300 dark:border-graphite-600 bg-white dark:bg-graphite-800 text-deepTechBlue-500 dark:text-light-50 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder={t('tokens.contactMessagePlaceholder')}
                {...register('message', {
                  required: t('tokens.contactMessageRequired'),
                })}
              />
              {errors.message?.message && (
                <p className="mt-1 text-sm text-error-600 dark:text-error-400">
                  {errors.message.message}
                </p>
              )}
            </div>
            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowContactForm(false);
                  reset();
                }}
              >
                {t('common.back')}
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={isSubmitting}
              >
                {t('tokens.contactSubmit')}
              </Button>
            </div>
          </form>
        ) : (
          <>
        {subscription?.has_subscription && (
          <div className="mb-3 rounded-lg border border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20 p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                <div>
                  <p className="text-sm font-medium text-green-700 dark:text-green-300">
                    {t('subscription.currentPlan')}: {subscription.package_name || '-'}
                  </p>
                  <p className="text-xs text-green-700/80 dark:text-green-300/80">
                    {t('subscription.status')}: {subscription.status || '-'}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadBillingData(true)}
                loading={isRefreshing}
              >
                <RefreshCw className="h-4 w-4 mr-1.5" />
                {t('common.update')}
              </Button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
            <span className="ml-3 text-gray-600 dark:text-gray-400">
              {t('subscription.loadingPlans')}
            </span>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <Button onClick={() => loadBillingData()} variant="outline">
              {t('common.retry')}
            </Button>
          </div>
        ) : packages.length === 0 ? (
          <div className="text-center py-12">
            <Coins className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
            <p className="text-gray-600 dark:text-gray-400">
              {t('subscription.noPlansAvailable')}
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 flex-shrink-0">
              {t('subscription.selectPlan')}
            </p>
            <div className="overflow-y-auto flex-1 pr-2 -mr-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pb-2">
                {packages.map(pkg => {
                  const isCurrent = currentPackageId === pkg.id;

                  return (
                    <Card
                      key={pkg.id}
                      className={`hover:shadow-lg transition-shadow border-2 ${
                        isCurrent
                          ? 'border-green-500 dark:border-green-400'
                          : 'hover:border-primary-500 dark:hover:border-primary-400'
                      }`}
                    >
                      <div className="flex flex-col h-full">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1 line-clamp-1">
                              {pkg.name}
                            </h3>
                            {pkg.description && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                                {pkg.description}
                              </p>
                            )}
                          </div>
                          <div className="ml-2 flex-shrink-0">
                            <Coins className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            {t('subscription.planPrice')}:
                          </span>
                          <span className="text-base font-bold text-gray-900 dark:text-white">
                            {formatPrice(pkg)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg mt-2">
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            {t('subscription.billingCycle')}:
                          </span>
                          <span className="text-sm font-semibold text-primary-700 dark:text-primary-300">
                            {intervalLabel(pkg)}
                          </span>
                        </div>

                        <div className="mt-3">
                          {isCurrent ? (
                            <Button className="w-full text-sm py-2" variant="success" size="sm" disabled>
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              {t('subscription.currentPlan')}
                            </Button>
                          ) : (
                            <Button
                              onClick={() => handleCheckoutSubscription(pkg)}
                              className="w-full text-sm py-2"
                              variant="primary"
                              size="sm"
                              loading={purchasingPackageId === pkg.id}
                              disabled={purchasingPackageId !== null}
                            >
                              <ShoppingCart className="h-4 w-4 mr-2" />
                              {purchasingPackageId === pkg.id
                                ? t('subscription.processingCheckout')
                                : t('subscription.subscribe')}
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
                <Card className="border-2 border-dashed border-primary-300 dark:border-primary-700 bg-primary-50/40 dark:bg-primary-900/10">
                  <div className="flex h-full flex-col">
                    <div className="mb-2">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                        {t('subscription.customPlanTitle')}
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {t('subscription.customPlanDescription')}
                      </p>
                    </div>
                    <div className="flex-1 rounded-lg border border-primary-100 dark:border-primary-800 bg-white dark:bg-gray-800 p-2.5">
                      <p className="text-sm font-semibold text-primary-700 dark:text-primary-300">
                        {t('subscription.customPlanCtaHint')}
                      </p>
                    </div>
                    <div className="mt-3">
                      <Button
                        className="w-full text-sm py-2"
                        size="sm"
                        onClick={() => setShowContactForm(true)}
                      >
                        {t('tokens.contactEnterprise')}
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </>
        )}
          </>
        )}
      </div>
    </Modal>
  );
};
